<template>
  <div class="chessboard-view">
    <Transition name="board-fade">
      <div v-if="!showLoader && board" class="board-content">
        <div class="board-frame">
          <div class="board-header">
            <div class="board-header-left">
              <span v-if="spotName" class="board-spot-name clickable" @click="handleGoToSpot">
                <GamIcon class="board-spot-pin" :name="GamIconName.PIN" size="12px" is-current />
                <span class="board-spot-label">{{ spotName }}</span>
              </span>
              <span v-if="board.type === ChessboardType.WORLD" class="board-type-badge" :class="[board.type]">
                {{ localize(`chessboard.type.${board.type}`) }}
              </span>
              <span v-if="planterName" class="board-planter clickable" @click="handleGoToPlanter">{{
                planterName
              }}</span>
              <span
                v-if="
                  !canPlay && !canAcceptPendingAction && !cooldownEndsAt && board.status === ChessboardStatus.ACTIVE
                "
                class="spectator-badge"
              >
                {{ localize('chessboard.spectator') }}
              </span>
            </div>
            <GamGambitOwner v-if="isPlanter" is-club-organizer />
          </div>

          <div
            v-if="board.type === ChessboardType.WORLD && board.chessboardLocations?.length"
            class="world-spots-strip"
          >
            <div
              v-for="cl in board.chessboardLocations"
              :key="cl.id"
              class="world-spot-chip"
              @click="handleGoToLinkedSpot(cl.locationId)"
            >
              <GamIcon :name="GamIconName.LOCATION" size="12px" is-current />
              <span class="world-spot-name">{{ cl.location?.name ?? '' }}</span>
            </div>
          </div>

          <div class="board-area">
            <div class="board-inner">
              <GamChessboard
                ref="boardRef"
                :fen="board.fen"
                :is-animated="true"
                :is-interactive="canPlay && !isSubmittingMove"
                :last-move-from="lastMoveFrom"
                :last-move-to="lastMoveTo"
                @move="onMove"
              />
              <div
                v-if="isCompleted && recentMoves.length"
                :class="['play-overlay', { 'is-playing': isPlaying }]"
                @click="togglePlayback"
              >
                <div class="play-overlay-btn">
                  <GamIcon
                    :name="isPlaying ? GamIconName.PAUSE : GamIconName.PLAY_EMPTY"
                    :size="GamIconSize.X_LARGE"
                    is-current
                  />
                </div>
              </div>
              <div
                v-if="needsLocationPrompt"
                :class="['board-location-overlay', { interactive: !access.hasGps.value }]"
                @click="handleLocationPrompt"
              >
                <GamIcon :name="GamIconName.NAVIGATE_EMPTY" :size="GamIconSize.LARGE" is-current />
                <span v-if="!access.hasGps.value">{{ localize('spot.proximity.allow_location') }}</span>
                <span v-else>{{ localize('spot.proximity.get_closer') }}</span>
              </div>
            </div>
          </div>

          <div class="board-below">
            <div class="board-footer-row">
              <div class="turn-indicator">
                <div :class="['turn-dot', currentTurn === 'w' ? 'white' : 'black']" />
                <span :class="['turn-label', { 'is-error': moveError, 'is-submitting': isSubmittingMove }]">{{
                  turnLabel
                }}</span>
              </div>

              <div v-if="showGameActions" class="board-game-actions">
                <button class="game-action-btn resign-btn" @click="handleResign">
                  {{ localize('chessboard.action.resign') }}
                </button>
                <button class="game-action-btn" @click="handleOfferDraw">
                  {{ localize('chessboard.action.offer_draw') }}
                </button>
              </div>

              <div v-else-if="pendingActionMessage && isOwnPendingAction" class="board-game-actions pending">
                <span class="pending-action-label">{{ pendingActionMessage }}</span>
                <button v-if="canCancelAction" class="pending-icon-btn cancel" @click="handleCancelAction">
                  <GamIcon :name="GamIconName.X_CLOSE" :size="GamIconSize.SMALL" is-current />
                </button>
              </div>

              <div v-else-if="pendingActionMessage && canAcceptPendingAction" class="board-game-actions pending">
                <span class="pending-action-label">{{ pendingActionMessage }}</span>
                <button class="pending-icon-btn accept" @click="handleAcceptAction">
                  <GamIcon :name="GamIconName.CHECK" :size="GamIconSize.SMALL" is-current />
                </button>
                <button class="pending-icon-btn cancel" @click="dismissPendingAction">
                  <GamIcon :name="GamIconName.X_CLOSE" :size="GamIconSize.SMALL" is-current />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div v-if="waitingBannerLabel" class="waiting-banner">
          <span class="waiting-dot" />
          {{ waitingBannerLabel }}
        </div>

        <div v-if="isCompleted && playbackBubbles.length" ref="bubblesContainerRef" class="move-bubbles">
          <div
            v-for="mv in playbackBubbles"
            :key="mv.id"
            :class="['bubble-wrap', mv.color === 'white' ? 'bubble-left' : 'bubble-right']"
          >
            <div :class="['move-bubble', { 'is-action': mv.isAction }]">
              <span class="bubble-text">{{ mv.label }}</span>
            </div>
            <span v-if="mv.isOwn" class="bubble-author">
              {{ localize('chessboard.bubble.me') }}
            </span>
          </div>
        </div>

        <div v-else-if="!isCompleted && lastTwoPlayerEvents.length" class="move-bubbles">
          <div
            v-for="mv in lastTwoPlayerEvents"
            :key="mv.id"
            :class="['bubble-wrap', mv.color === 'white' ? 'bubble-left' : 'bubble-right']"
          >
            <div
              :class="['move-bubble', { 'is-action': mv.isAction }]"
              @click="!mv.isAction && mv.moveNumber != null && handleReplay(mv.moveNumber)"
            >
              <span class="bubble-text">{{ mv.label }}</span>
            </div>
            <span v-if="mv.isOwn" class="bubble-author">
              {{ localize('chessboard.bubble.me') }}
            </span>
          </div>
        </div>
      </div>
    </Transition>

    <div v-if="isNotFound" class="board-error">
      {{ localize('chessboard.removed') }}
    </div>

    <div v-else-if="isError" class="board-error">
      {{ localize('chessboard.not_found') }}
    </div>

    <div v-if="board" class="nav-bar">
      <div class="nav-close-btn" @click="handleClose">
        <GamIcon :name="GamIconName.X_CLOSE" :size="GamIconSize.X_LARGE" is-current />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { ChessboardActionLogDto, ChessMoveDto } from '@/core/data/chessboard/chessboard.interface';
import {
  ChessboardActionType,
  ChessboardPendingAction,
  ChessboardStatus,
  ChessboardType,
} from '@/core/data/chessboard/chessboard.interface';
import { localize, localizeWithValues } from '@/core/gambits';
import { getPoeticTimestamp, getTimeRemaining } from '@/core/helpers/date.helper';
import type { ApiError } from '@/core/network/http/httpClient.interface';
import { useAuthStore } from '@/stores/auth.store';
import { useChessboardStore } from '@/stores/chessboard.store';
import { useGeolocationStore } from '@/stores/geolocation.store';
import { useLocationStore } from '@/stores/location.store';
import GamChessboard from '@/views/components/gam-chessboard/GamChessboard.vue';
import GamGambitOwner from '@/views/components/gam-gambits/GamGambitOwner.vue';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamIconName, GamIconSize } from '@/views/composables/constants/components/gamIcon.constants';
import { useAccessLevel } from '@/views/composables/hooks/useAccessLevel';
import { rememberLocationFunction, requestLocationFor } from '@/views/composables/hooks/useLocationAsk';
import type { ChessboardMoveEvent } from '@/views/composables/models/components/GamChessboard';

import { Chess } from 'chess.js';
import { storeToRefs } from 'pinia';
import { computed, nextTick, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

const route = useRoute();
const router = useRouter();
const chessboardStore = useChessboardStore();
const geolocationStore = useGeolocationStore();
const authStore = useAuthStore();
const locationStore = useLocationStore();

const { board } = storeToRefs(chessboardStore);
const { user: authUser, userDetails } = storeToRefs(authStore);
const { location: spotLocation } = storeToRefs(locationStore);

const spotCoords = computed(() => spotLocation.value?.coordinates);
const access = useAccessLevel(spotCoords);

const boardRef = ref<InstanceType<typeof GamChessboard>>();
const bubblesContainerRef = ref<HTMLDivElement>();
const isSubmittingMove = ref(false);
const moveError = ref<string | null>(null);
const lastMoveFrom = ref<string | undefined>();
const lastMoveTo = ref<string | undefined>();
const isReplaying = ref(false);
const recentMoves = ref<ChessMoveDto[]>([]);
const actionLogs = ref<ChessboardActionLogDto[]>([]);

// Advances once a minute so an open board stops waiting on its own once the
// 20h cooldown runs out, without needing a refetch.
const cooldownTick = ref(Date.now());
const cooldownTimer = setInterval(() => (cooldownTick.value = Date.now()), 60_000);

const playbackIndex = ref(-1);
const isPlaying = ref(false);
const playbackTimeMs = ref(0);
let playbackRafId: number | null = null;
let playbackLastFrame = 0;
let playbackTimeline: number[] = [];
const playbackTotalMs = ref(0);

const isLoading = computed(() => chessboardStore.isLoading);
const isError = computed(() => chessboardStore.isError);
const isNotFound = computed(() => chessboardStore.isNotFound);

const accessResolved = computed(() => !!spotCoords.value || !board.value);
const showLoader = computed(() => isLoading.value || (board.value && !accessResolved.value));

const boardId = computed(() => {
  const id = route.params.id;
  return typeof id === 'string' ? id : undefined;
});

const spotName = computed(() => {
  if (spotLocation.value?.name) return spotLocation.value.name;
  if (!board.value?.chessboardLocations?.length) return '';
  return board.value.chessboardLocations[0].location?.name ?? '';
});

const needsLocationPrompt = computed(() => {
  if (!board.value || board.value.status !== ChessboardStatus.ACTIVE) return false;
  if (!geolocationStore.isPermissionResolved) return false;
  if (!access.hasGps.value) return true;
  if (spotCoords.value && !access.isNearby.value) return true;
  return false;
});

const planterName = computed(() => {
  if (!board.value?.plantedByUser?.username) return '';
  if (board.value.isVetted) return '';
  return board.value.plantedByUser.username;
});

const isPlanter = computed(() => {
  if (!board.value?.plantedByUserId || !authUser.value?.id) return false;
  return board.value.plantedByUserId === authUser.value.id;
});

const currentTurn = computed(() => {
  if (!board.value) return 'w';
  return board.value.fen.split(' ')[1] || 'w';
});

const hasGpsAccess = computed(() => {
  if (!board.value || !authUser.value) return false;
  if (board.value.status !== ChessboardStatus.ACTIVE) return false;
  if (!access.canPlayBoards.value) return false;
  return true;
});

// A board allows each player one act per 20 hours, so the wait is measured
// against the viewer's own last act rather than against whose turn it is.
const cooldownEndsAt = computed(() => {
  const nextMoveAt = board.value?.nextMoveAt;
  if (!nextMoveAt) return null;
  return new Date(nextMoveAt).getTime() > cooldownTick.value ? nextMoveAt : null;
});

const canMakeMove = computed(() => {
  if (!hasGpsAccess.value || !board.value || !authUser.value) return false;
  return !cooldownEndsAt.value;
});

const canPlay = computed(() => canMakeMove.value);

const showGameActions = computed(
  () =>
    canMakeMove.value &&
    board.value?.status === ChessboardStatus.ACTIVE &&
    !pendingActionMessage.value &&
    !needsLocationPrompt.value,
);

const waitingBannerLabel = computed(() => {
  if (!board.value || !authUser.value) return '';
  if (board.value.status !== ChessboardStatus.ACTIVE) return '';
  if (!cooldownEndsAt.value) return '';

  return localizeWithValues('chessboard.move_again_in', [getTimeRemaining(cooldownEndsAt.value)]);
});

interface TimelineEvent {
  id: string;
  userId: string;
  createdAt: string;
  type: 'move' | 'action';
  moveNumber?: number;
  color?: string;
  actionType?: ChessboardActionType;
  locationId?: string;
}

type PendingOfferType = 'draw' | 'resignation';

// Cancel/decline are stored as generic actions with no record of what offer
// they resolved, so we map each one to the most recent preceding offer in the
// timeline to label it as a draw or a resignation.
function buildOfferTypeMap(timeline: TimelineEvent[]): Map<string, PendingOfferType> {
  const map = new Map<string, PendingOfferType>();
  let lastOffer: PendingOfferType | null = null;
  for (const ev of timeline) {
    if (ev.type !== 'action') continue;
    switch (ev.actionType) {
      case ChessboardActionType.OFFER_DRAW:
        lastOffer = 'draw';
        break;
      case ChessboardActionType.RESIGN:
        lastOffer = 'resignation';
        break;
      case ChessboardActionType.CANCEL_ACTION:
      case ChessboardActionType.DECLINE_ACTION:
        if (lastOffer) map.set(ev.id, lastOffer);
        lastOffer = null;
        break;
      case ChessboardActionType.ACCEPT_DRAW:
      case ChessboardActionType.ACCEPT_RESIGNATION:
        lastOffer = null;
        break;
    }
  }
  return map;
}

function getActionBubbleLabel(actionType: ChessboardActionType, offerType?: PendingOfferType): string {
  switch (actionType) {
    case ChessboardActionType.RESIGN:
      return localize('chessboard.bubble.offered_resignation');
    case ChessboardActionType.OFFER_DRAW:
      return localize('chessboard.bubble.offered_draw');
    case ChessboardActionType.ACCEPT_RESIGNATION:
      return localize('chessboard.bubble.accepted_resignation');
    case ChessboardActionType.ACCEPT_DRAW:
      return localize('chessboard.bubble.accepted_draw');
    case ChessboardActionType.CANCEL_ACTION:
      return offerType === 'resignation'
        ? localize('chessboard.bubble.resignation_cancelled')
        : localize('chessboard.bubble.draw_cancelled');
    case ChessboardActionType.DECLINE_ACTION:
      return offerType === 'resignation'
        ? localize('chessboard.bubble.resignation_declined')
        : localize('chessboard.bubble.draw_declined');
    default:
      return '';
  }
}

function buildUnifiedTimeline(moves: ChessMoveDto[], actions: ChessboardActionLogDto[]): TimelineEvent[] {
  const events: TimelineEvent[] = [];
  for (const mv of moves) {
    events.push({
      id: mv.id,
      userId: mv.userId,
      createdAt: mv.createdAt,
      type: 'move',
      moveNumber: mv.moveNumber,
      color: mv.color,
      locationId: mv.locationId,
    });
  }
  for (const a of actions) {
    events.push({
      id: a.id,
      userId: a.userId,
      createdAt: a.createdAt,
      type: 'action',
      color: a.color,
      actionType: a.actionType,
    });
  }
  events.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  return events;
}

function getLocationLabel(locationId?: string): string {
  if (!locationId || board.value?.type !== ChessboardType.WORLD) return '';
  const loc = board.value?.chessboardLocations?.find((cl) => cl.locationId === locationId);
  return loc?.location?.name ? ` from ${loc.location.name}` : '';
}

function eventToBubble(event: TimelineEvent, userEmoji?: string, offerType?: PendingOfferType) {
  const isOwn = event.userId === authUser.value?.id;
  const color = event.color ?? 'white';
  if (event.type === 'move') {
    const poeticTime = getPoeticTimestamp(event.createdAt);
    const locationSuffix = getLocationLabel(event.locationId);
    return {
      id: event.id,
      userId: event.userId,
      moveNumber: event.moveNumber,
      color,
      isOwn,
      isAction: false,
      emoji: isOwn ? userEmoji : undefined,
      label:
        localizeWithValues('chessboard.bubble.shared_move', [String(event.moveNumber ?? ''), poeticTime]) +
        locationSuffix,
    };
  }
  return {
    id: event.id,
    userId: event.userId,
    moveNumber: undefined,
    color,
    isOwn,
    isAction: true,
    emoji: isOwn ? userEmoji : undefined,
    label: `${getActionBubbleLabel(event.actionType!, offerType)}: ${getPoeticTimestamp(event.createdAt)}`,
  };
}

const lastTwoPlayerEvents = computed(() => {
  const timeline = buildUnifiedTimeline(recentMoves.value, actionLogs.value);
  if (!timeline.length) return [];

  const offerTypeMap = buildOfferTypeMap(timeline);
  const userEmoji = userDetails.value?.emoji;
  const result: TimelineEvent[][] = [];
  let currentGroup: TimelineEvent[] = [];
  let currentUserId: string | null = null;

  for (const ev of timeline) {
    if (ev.userId !== currentUserId) {
      if (currentGroup.length) result.push(currentGroup);
      currentGroup = [ev];
      currentUserId = ev.userId;
    } else {
      currentGroup.push(ev);
    }
  }
  if (currentGroup.length) result.push(currentGroup);

  const lastTwo = result.slice(-2);
  const bubbles: ReturnType<typeof eventToBubble>[] = [];
  for (const group of lastTwo) {
    for (const ev of group) {
      bubbles.push(eventToBubble(ev, userEmoji, offerTypeMap.get(ev.id)));
    }
  }
  return bubbles;
});

const isCompleted = computed(() => board.value?.status === ChessboardStatus.COMPLETED);

const fullTimeline = computed(() => buildUnifiedTimeline(recentMoves.value, actionLogs.value));

const playbackBubbles = computed(() => {
  if (playbackIndex.value < 0 || !fullTimeline.value.length) return [];
  const userEmoji = userDetails.value?.emoji;
  const offerTypeMap = buildOfferTypeMap(fullTimeline.value);
  return fullTimeline.value
    .slice(0, playbackIndex.value + 1)
    .map((ev) => eventToBubble(ev, userEmoji, offerTypeMap.get(ev.id)));
});

const turnLabel = computed(() => {
  if (isSubmittingMove.value) return localize('chessboard.submitting');
  if (moveError.value) return moveError.value;
  if (!board.value) return '';
  if (board.value.status === ChessboardStatus.COMPLETED) {
    const winner =
      board.value.result === 'white_win'
        ? localize('chessboard.demo.white')
        : board.value.result === 'black_win'
          ? localize('chessboard.demo.black')
          : null;
    if (board.value.result === 'white_win' || board.value.result === 'black_win') {
      let reason = localize('chessboard.demo.resignation');
      try {
        const chess = new Chess(board.value.fen);
        if (chess.isCheckmate()) reason = localize('chessboard.demo.checkmate');
      } catch {
        /* ignore */
      }
      return `${reason}, ${winner} ${localize('chessboard.result.wins')}`;
    }
    if (board.value.result === 'stalemate') return localize('chessboard.demo.stalemate');
    if (board.value.result === 'draw') return localize('chessboard.demo.draw');
  }
  return currentTurn.value === 'w'
    ? localize('chessboard.demo.white_to_move')
    : localize('chessboard.demo.black_to_move');
});

const pendingActionMessage = computed(() => {
  if (!board.value) return '';
  if (board.value.pendingAction === ChessboardPendingAction.RESIGNATION_OFFERED) {
    return localize('chessboard.action.resign_offered');
  }
  if (board.value.pendingAction === ChessboardPendingAction.DRAW_OFFERED) {
    return localize('chessboard.action.draw_offered');
  }
  return '';
});

const isOwnPendingAction = computed(() => {
  if (!board.value || !authUser.value) return false;
  return board.value.pendingActionByUserId === authUser.value.id;
});

const canCancelAction = computed(() => {
  return isOwnPendingAction.value;
});

// Answering an offer does not start a new 20h clock, but it still needs the
// current one to be up: it is this player's one act in the window.
const canAcceptPendingAction = computed(() => {
  if (!hasGpsAccess.value || !board.value || !authUser.value) return false;
  if (cooldownEndsAt.value) return false;
  return board.value.pendingActionByUserId !== authUser.value.id;
});

function getLocationId(): string | undefined {
  if (!board.value) return undefined;
  const queryLoc = route.query.loc as string | undefined;
  return queryLoc ?? board.value.lastMoveAtLocationId ?? board.value.chessboardLocations?.[0]?.locationId;
}

async function refreshTimeline(chessboardId: string) {
  const [movesResult, actionsResult] = await Promise.all([
    chessboardStore.fetchMoves(chessboardId),
    chessboardStore.fetchActionLogs(chessboardId),
  ]);
  recentMoves.value = movesResult?.data ?? [];
  actionLogs.value = actionsResult?.data ?? [];
}

// A null entry resolves itself: the board is refetched on every rejection, so
// the completed or updated state speaks for itself and a label would only
// contradict it.
const MOVE_ERROR_LABELS: Record<string, string | null> = {
  chessboard_game_over: null,
  chessboard_version_conflict: null,
  chessboard_illegal_move: 'chessboard.error.illegal_move',
  chessboard_gps_uncertain: 'chessboard.error.gps_uncertain',
};

function moveErrorLabel(error?: ApiError): string | null {
  if (error?.errorKey === 'chessboard_too_far' && error.distanceM != null) {
    return localizeWithValues('chessboard.error.too_far', [String(error.distanceM)]);
  }

  if (error?.errorKey === 'chessboard_move_cooldown' && error.nextMoveAt) {
    return localizeWithValues('chessboard.move_again_in', [getTimeRemaining(error.nextMoveAt)]);
  }

  const translationKey = error?.errorKey ? MOVE_ERROR_LABELS[error.errorKey] : undefined;
  if (translationKey === null) return null;

  return localize(translationKey ?? 'chessboard.error.move_failed');
}

async function onMove(event: ChessboardMoveEvent) {
  if (!board.value || !canPlay.value) return;

  const userLoc = geolocationStore.userLocation;
  if (!userLoc?.location) return;

  const locationId = getLocationId();
  if (!locationId) return;

  isSubmittingMove.value = true;
  moveError.value = null;

  const result = await chessboardStore.makeMove(board.value.id, {
    from: event.from,
    to: event.to,
    promotion: event.promotion,
    expectedVersion: board.value.version,
    lat: userLoc.location.lat,
    lng: userLoc.location.lng,
    gpsAccuracy: userLoc.accuracy,
    locationId,
  });

  isSubmittingMove.value = false;

  if (result?.data) {
    rememberLocationFunction();
    lastMoveFrom.value = event.from;
    lastMoveTo.value = event.to;
    await refreshTimeline(result.data.id);
  } else {
    moveError.value = moveErrorLabel(result?.error);
    if (boardId.value) {
      await chessboardStore.fetchBoard(boardId.value);
    }
  }
}

const ANIM_SLIDE_MS = 1200;
const ANIM_START_DELAY_MS = 1000;

async function animateForward(moveIndex: number, allMoves: ChessMoveDto[]) {
  if (!boardRef.value) return;

  const targetMove = allMoves[moveIndex];
  const prevFen =
    moveIndex > 0 ? allMoves[moveIndex - 1].fen : 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

  const chess = new Chess(prevFen);
  const parsed = chess.move(targetMove.move);
  if (!parsed) return;

  lastMoveFrom.value = undefined;
  lastMoveTo.value = undefined;
  await nextTick();
  await new Promise((resolve) => setTimeout(resolve, 500));
  lastMoveFrom.value = parsed.from;
  await nextTick();
  await new Promise((resolve) => setTimeout(resolve, 500));
  lastMoveTo.value = parsed.to;
  await nextTick();
  await new Promise((resolve) => setTimeout(resolve, ANIM_START_DELAY_MS));
  await boardRef.value.animateMove(parsed.from, parsed.to, getPieceCode(parsed), ANIM_SLIDE_MS);
  boardRef.value.engine.loadFen(targetMove.fen);
}

async function handleReplay(targetMoveNumber?: number) {
  if (!board.value || !boardRef.value || isReplaying.value) return;

  isReplaying.value = true;
  const allMoves = recentMoves.value.length
    ? recentMoves.value
    : (await chessboardStore.fetchMoves(board.value.id))?.data;
  if (!allMoves?.length) {
    isReplaying.value = false;
    return;
  }

  const moveIndex =
    targetMoveNumber != null ? allMoves.findIndex((m) => m.moveNumber === targetMoveNumber) : allMoves.length - 1;
  if (moveIndex < 0) {
    isReplaying.value = false;
    return;
  }

  const startFen =
    moveIndex > 0 ? allMoves[moveIndex - 1].fen : 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
  lastMoveFrom.value = undefined;
  lastMoveTo.value = undefined;
  boardRef.value.engine.loadFen(startFen);
  await nextTick();
  await new Promise((resolve) => setTimeout(resolve, ANIM_START_DELAY_MS));

  try {
    for (let i = moveIndex; i < allMoves.length; i++) {
      await animateForward(i, allMoves);
    }
  } catch {
    // restore on error
  }

  boardRef.value.engine.loadFen(board.value.fen);
  lastMoveFrom.value = undefined;
  lastMoveTo.value = undefined;

  isReplaying.value = false;
}

function getPieceCode(move: { color: string; piece: string }): string {
  return move.color === 'w' ? move.piece.toUpperCase() : move.piece.toLowerCase();
}

const BASE_MS_PER_MOVE = 1500;

function buildTimeline() {
  const events = fullTimeline.value;
  if (!events.length) {
    playbackTimeline = [];
    playbackTotalMs.value = 0;
    return;
  }

  const positions: number[] = [];
  for (let i = 0; i < events.length; i++) {
    positions.push(500 + i * BASE_MS_PER_MOVE);
  }

  playbackTimeline = positions;
  playbackTotalMs.value = positions[positions.length - 1] + 500;
}

function seekToMove(index: number) {
  if (!boardRef.value || !recentMoves.value.length) return;
  const startFen = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

  if (index < 0) {
    boardRef.value.engine.loadFen(startFen);
    lastMoveFrom.value = undefined;
    lastMoveTo.value = undefined;
    playbackIndex.value = -1;
    playbackTimeMs.value = 0;
    return;
  }

  const mv = recentMoves.value[index];
  if (!mv) return;

  boardRef.value.engine.loadFen(mv.fen);
  lastMoveFrom.value = undefined;
  lastMoveTo.value = undefined;
  playbackIndex.value = index;
  playbackTimeMs.value = playbackTimeline[index] ?? 0;
}

function stopPlayback() {
  isPlaying.value = false;
  if (playbackRafId) {
    cancelAnimationFrame(playbackRafId);
    playbackRafId = null;
  }
}

function applyTimelineEvent(index: number) {
  const events = fullTimeline.value;
  const event = events[index];
  if (!event) return;

  playbackIndex.value = index;

  if (event.type === 'action') return;

  if (!boardRef.value) return;
  const mv = recentMoves.value.find((m) => m.id === event.id);
  if (!mv) return;

  const startFen = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
  const mvIndex = recentMoves.value.indexOf(mv);
  const prevFen = mvIndex > 0 ? recentMoves.value[mvIndex - 1].fen : startFen;
  const chess = new Chess(prevFen);
  const parsed = chess.move(mv.move);
  if (!parsed) return;

  boardRef.value.engine.loadFen(mv.fen);
  lastMoveFrom.value = undefined;
  lastMoveTo.value = undefined;
}

function playbackTick(now: number) {
  if (!isPlaying.value) return;

  const delta = now - playbackLastFrame;
  playbackLastFrame = now;
  playbackTimeMs.value += delta;

  if (playbackTimeMs.value >= playbackTotalMs.value) {
    playbackTimeMs.value = playbackTotalMs.value;
    const lastIdx = fullTimeline.value.length - 1;
    if (playbackIndex.value < lastIdx) {
      applyTimelineEvent(lastIdx);
    }
    stopPlayback();
    return;
  }

  const nextIdx = playbackIndex.value + 1;
  if (nextIdx < playbackTimeline.length && playbackTimeMs.value >= playbackTimeline[nextIdx]) {
    applyTimelineEvent(nextIdx);
  }

  playbackRafId = requestAnimationFrame(playbackTick);
}

function togglePlayback() {
  if (isPlaying.value) {
    stopPlayback();
    return;
  }

  buildTimeline();
  if (!playbackTimeline.length) return;

  if (playbackIndex.value >= fullTimeline.value.length - 1) {
    seekToMove(-1);
  }

  isPlaying.value = true;
  playbackLastFrame = performance.now();
  playbackRafId = requestAnimationFrame(playbackTick);
}

async function performGpsAction(action: ChessboardActionType) {
  if (!board.value || !hasGpsAccess.value) return;

  const userLoc = geolocationStore.userLocation;
  if (!userLoc?.location) return;

  const locationId = getLocationId();
  if (!locationId) return;

  const boardId = board.value.id;
  const result = await chessboardStore.performAction(boardId, {
    action,
    lat: userLoc.location.lat,
    lng: userLoc.location.lng,
    locationId,
  });

  if (result?.data) {
    await chessboardStore.fetchBoard(boardId);
    const actionsResult = await chessboardStore.fetchActionLogs(boardId);
    actionLogs.value = actionsResult?.data ?? [];
  }
}

async function handleResign() {
  await performGpsAction(ChessboardActionType.RESIGN);
}

async function handleOfferDraw() {
  await performGpsAction(ChessboardActionType.OFFER_DRAW);
}

async function handleAcceptAction() {
  if (!board.value) return;
  const action =
    board.value.pendingAction === ChessboardPendingAction.RESIGNATION_OFFERED
      ? ChessboardActionType.ACCEPT_RESIGNATION
      : ChessboardActionType.ACCEPT_DRAW;
  await performGpsAction(action);
}

async function handleCancelAction() {
  if (!board.value || !authUser.value) return;

  const userLoc = geolocationStore.userLocation;
  if (!userLoc?.location) return;

  const locationId = getLocationId();
  if (!locationId) return;

  const boardId = board.value.id;
  await chessboardStore.performAction(boardId, {
    action: ChessboardActionType.CANCEL_ACTION,
    lat: userLoc.location.lat,
    lng: userLoc.location.lng,
    locationId,
  });
  await chessboardStore.fetchBoard(boardId);
}

async function dismissPendingAction() {
  await performGpsAction(ChessboardActionType.DECLINE_ACTION);
}

function handleGoToSpot() {
  const locationId = spotLocation.value?.id ?? board.value?.chessboardLocations?.[0]?.locationId;
  if (locationId) {
    router.push({ path: '/discover', query: { spot: locationId } });
  }
}

function handleGoToPlanter() {
  const username = board.value?.plantedByUser?.username;
  if (username) {
    router.push({ path: `/@${username}` });
  }
}

function handleGoToLinkedSpot(locationId: string) {
  router.push({ path: '/discover', query: { spot: locationId } });
}

function handleClose() {
  const locationId = route.query.loc as string | undefined;
  const fallbackLocationId = board.value?.chessboardLocations?.[0]?.locationId;
  const from = route.query.from as string | undefined;

  if (from === 'sphere-completed') {
    router.back();
    return;
  } else if (from === 'boards' && userDetails.value?.username) {
    router.push({ path: `/@${userDetails.value.username}` });
  } else if (window.history.length > 1) {
    router.back();
  } else if (locationId || fallbackLocationId) {
    router.push({ path: '/locations', query: { spot: locationId || fallbackLocationId } });
  } else {
    router.push({ path: '/locations' });
  }
}

watch(
  boardId,
  async (id) => {
    if (id) {
      lastMoveFrom.value = undefined;
      lastMoveTo.value = undefined;
      await chessboardStore.fetchBoard(id);

      if (geolocationStore.isLocationPermissionGranted) {
        geolocationStore.requestFreshPosition();
      }

      if (board.value) {
        await refreshTimeline(id);

        if (board.value.status === ChessboardStatus.COMPLETED) {
          stopPlayback();
          playbackIndex.value = -1;
          playbackTimeMs.value = 0;
          buildTimeline();
          await nextTick();
          boardRef.value?.engine.loadFen('rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1');
          lastMoveFrom.value = undefined;
          lastMoveTo.value = undefined;
        }
      }

      const locationId = board.value?.chessboardLocations?.[0]?.locationId;
      if (locationId) {
        await locationStore.fetch(locationId);
      }
    } else {
      chessboardStore.clear();
      recentMoves.value = [];
      actionLogs.value = [];
    }
  },
  { immediate: true },
);

function handleLocationPrompt() {
  if (access.hasGps.value) return;
  requestLocationFor('boards', () => {
    void geolocationStore.requestFreshPosition();
  });
}

let lastSeenUpdatedAt: string | null = null;

watch(
  () => board.value?.updatedAt,
  async (updatedAt) => {
    if (!updatedAt || !board.value) return;
    const ts = String(updatedAt);
    if (lastSeenUpdatedAt && ts !== lastSeenUpdatedAt) {
      await refreshTimeline(board.value.id);
    }
    lastSeenUpdatedAt = ts;
  },
);

watch(playbackBubbles, async () => {
  await nextTick();
  if (bubblesContainerRef.value) {
    bubblesContainerRef.value.scrollTop = bubblesContainerRef.value.scrollHeight;
  }
});

onUnmounted(() => {
  stopPlayback();
  clearInterval(cooldownTimer);
  chessboardStore.clear();
});
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
.chessboard-view {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: var(--spacing-l);
  padding-top: calc(var(--sai-top) + 2rem);
  padding-bottom: 0;
  height: 100dvh;
  overflow: hidden;
  gap: var(--spacing-md);
  background: linear-gradient(
    20deg,
    oklch(0.29 0.025 var(--hue)) 0%,
    oklch(0.23 0.012 calc(var(--hue) + 90)) 50%,
    oklch(0.29 0.025 calc(var(--hue) + 180)) 100%
  );
}

.board-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-md);
  width: 100%;
  flex: 1;
  min-height: 0;
}

.board-fade-enter-active {
  transition:
    opacity 800ms ease 500ms,
    transform 800ms ease 500ms;
}

.board-fade-enter-from {
  opacity: 0;
  transform: scale(0.92);
}

.board-error {
  color: var(--color-white-50);
  font-size: 14px;
  font-weight: 500;
  margin-top: 4rem;
}

.board-frame {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  // Hug the board rather than measuring the header/footer, which can be wider
  // than the board on narrow screens: the same expression `.board-area` sizes
  // itself with, plus this frame's own padding and border (26px).
  width: calc(min(100vw - 58px, 100dvh - 226px, 534px) + 26px);
  flex-shrink: 0;
  background: rgba(0, 0, 0, 0.8);
  border-radius: 1rem;
  padding: 0.75rem;
  border: 1px solid var(--color-white-10);
}

.board-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.board-header-left {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  flex: 1;
}

.board-below {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.move-bubbles {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
  max-width: 560px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  scrollbar-width: none;
  padding: 0 0.75rem;
  padding-bottom: 6rem;
  scroll-behavior: smooth;

  &::-webkit-scrollbar {
    display: none;
  }
}

.bubble-wrap {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;

  &.bubble-left {
    align-self: flex-start;
    align-items: flex-start;
  }

  &.bubble-right {
    align-self: flex-end;
    align-items: flex-end;
  }
}

.move-bubble {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  border-radius: 1rem;
  cursor: pointer;
  transition: all 150ms ease;

  .bubble-left > & {
    background: #e5e5ea;
    border-bottom-left-radius: 4px;

    .bubble-text {
      color: rgba(0, 0, 0, 0.85);
    }

    .bubble-emoji {
      filter: none;
    }
  }

  .bubble-right > & {
    background: #1a1a1a;
    border-bottom-right-radius: 4px;

    .bubble-text {
      color: rgba(255, 255, 255, 0.95);
    }
  }

  @include hover {
    filter: brightness(0.92);
  }

  &.is-action {
    cursor: default;
    opacity: 0.85;
    font-style: italic;
  }
}

.bubble-text {
  font-size: 12px;
  font-weight: 400;
  font-style: italic;
}

.bubble-author {
  font-size: 10px;
  font-weight: 300;
  color: var(--color-white);
  background: var(--color-white-10);
  padding: 1px 8px;
  border-radius: 1rem;
}

// Sized exactly like the walk and squares boards' `.zone-board`: an explicit
// `min()` width paired with the same `min()` as `max-height`. Safari does not
// reliably recompute an aspect-ratio height from a percentage width inside a
// flex column during a live resize, which let the frame shrink away from the
// board and clip it.
// 58px  = view side padding (32) + frame padding and border (26).
// 226px = top padding + frame chrome, gaps, header, footer and close button.
.board-area {
  width: min(100vw - 58px, 100dvh - 226px, 534px);
  max-height: min(100vw - 58px, 100dvh - 226px, 534px);
  aspect-ratio: 1;
  min-height: 0;
  position: relative;
}

// Clone of the walk board's `.board-inner`: the chessboard resolves against an
// absolutely-positioned definite box rather than an aspect-ratio one.
.board-inner {
  position: absolute;
  inset: 0;
  border-radius: var(--radius-x-small);
  overflow: hidden;
}

.play-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 2;
  border-radius: var(--radius-x-small);
  transition: opacity 300ms ease;

  &.is-playing {
    opacity: 0;

    @include hover {
      opacity: 1;
    }
  }
}

.waiting-banner {
  align-self: center;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 16px;
  font-size: 13px;
  letter-spacing: 0.03em;
  color: var(--color-white-70);
  background: var(--color-dark-600);
  border: 1px solid var(--color-white-10);
  border-radius: var(--radius-small);
  white-space: nowrap;
  margin-top: 0.5rem;
}

.waiting-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #f1c40f;
  box-shadow: 0 0 6px rgba(241, 196, 15, 0.6);
  flex-shrink: 0;
}

.board-location-overlay {
  position: absolute;
  inset: 0;
  z-index: 6;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: var(--color-fabric-gradient-darker);
  color: var(--color-white-70);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.04em;
  border-radius: var(--radius-x-small);
  transition: all 150ms ease;

  &.interactive {
    cursor: pointer;

    @include hover {
      background: rgba(0, 0, 0, 0.7);
      color: var(--color-white);
    }
  }
}

.play-overlay-btn {
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(8px);
  border: 1px solid var(--color-white-20);
  border-radius: 50%;
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-white);
  transition: all 200ms ease;
  touch-action: manipulation;

  @include hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: var(--color-white-30);
  }
}

.board-spot-name {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  color: var(--color-white-80);
  font-size: 15px;
  font-weight: 600;
  max-width: 200px;

  .board-spot-pin {
    flex-shrink: 0;
  }

  .board-spot-label {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &.clickable {
    cursor: pointer;
    text-decoration: underline;
    text-decoration-color: var(--color-white-30);
    text-underline-offset: 3px;
    transition: all 150ms linear;

    @include hover {
      color: var(--color-white);
      text-decoration-color: var(--color-white-70);
    }
  }
}

.board-type-badge {
  font-size: 10px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 4px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  flex-shrink: 0;

  &.local {
    background: var(--color-white-10);
    color: var(--color-white-50);
  }

  &.world {
    background: var(--color-pink);
    color: var(--color-white);
  }
}

.world-spots-strip {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 0 var(--spacing-md) var(--spacing-sm);
}

.world-spot-chip {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 12px;
  background: var(--color-white-5);
  color: var(--color-white-40);
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 150ms ease;

  @include hover {
    background: var(--color-white-10);
    color: var(--color-white-60);
  }
}

.world-spot-name {
  white-space: nowrap;
}

.board-planter {
  font-size: 10px;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 4px;
  background: var(--color-white-10);
  color: var(--color-white-40);
  cursor: pointer;
}

.spectator-badge {
  font-size: 10px;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 4px;
  background: var(--color-white-10);
  color: var(--color-white-50);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  flex-shrink: 0;
}

.board-game-actions {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  flex-shrink: 0;

  &.pending {
    gap: 0.5rem;
  }
}

.game-action-btn {
  background: var(--color-white-5);
  border: 1px solid var(--color-white-10);
  border-radius: var(--radius-large);
  color: var(--color-white-50);
  padding: 3px 10px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 150ms ease;

  @include hover {
    background: var(--color-white-10);
    border-color: var(--color-white-20);
    color: var(--color-white-70);
  }

  &.resign-btn {
    color: rgba(231, 76, 60, 0.7);
    border-color: rgba(231, 76, 60, 0.15);

    @include hover {
      background: rgba(231, 76, 60, 0.1);
      border-color: rgba(231, 76, 60, 0.3);
      color: #e74c3c;
    }
  }

  @include press;
}

.pending-action-label {
  color: var(--color-white-80);
  font-size: 11px;
  font-weight: 600;
}

.pending-icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: 1px solid var(--color-white-15);
  border-radius: 50%;
  width: 22px;
  height: 22px;
  cursor: pointer;
  transition: all 150ms ease;
  flex-shrink: 0;

  &.accept {
    color: rgba(46, 204, 113, 0.7);
    border-color: rgba(46, 204, 113, 0.25);

    @include hover {
      color: #2ecc71;
      border-color: rgba(46, 204, 113, 0.5);
      background: rgba(46, 204, 113, 0.1);
    }
  }

  &.cancel {
    color: var(--color-white-40);

    @include hover {
      color: var(--color-white-70);
      border-color: var(--color-white-30);
      background: var(--color-white-5);
    }
  }
}

.board-footer-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.turn-indicator {
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.turn-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  transition: background 0.2s ease;

  &.white {
    background: var(--color-white);
    box-shadow: 0 0 6px var(--color-white-50);
  }

  &.black {
    background: #000;
    border: 1px solid var(--color-white-30);
  }
}

.turn-label {
  color: var(--color-white-70);
  font-size: 13px;
  font-weight: 500;

  &.is-submitting {
    color: var(--color-white-40);
  }

  &.is-error {
    color: #e74c3c;
  }
}

.nav-bar {
  position: fixed;
  bottom: var(--safe-bottom-offset);
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  pointer-events: none;
}

.nav-close-btn {
  border-radius: 3.5rem;
  border: 1px solid var(--color-white-10);
  backdrop-filter: blur(8px);
  background-color: var(--color-dark-5);
  color: var(--color-white-70);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 200ms linear;
  pointer-events: auto;
  width: 3.5rem;
  height: 3.5rem;

  @include hover {
    background-color: var(--color-dark-20);
    border-color: var(--color-white-30);
    color: var(--color-white);
  }

  @include press;
}
</style>
